/**
 * VideoconferenceToggle is the owner-only "turn videoconferencing on/off for this
 * room" control, shared between the Room Menu and the "Me" dialog's Audio and
 * video tab so both stay in sync with the same interaction.
 */
import * as React from 'react'
import { Switch } from '@merels/ui'

export interface VideoconferenceToggleProps {
  videoEnabled: boolean
  /** Should handle its own errors (e.g. show a banner) - this component only manages the saving indicator. */
  onToggle: (next: boolean) => Promise<void>
  label?: string
  testId?: string
  /** Extra classes for the underlying Switch, e.g. a controller-nav focus ring. */
  className?: string
}

export const VideoconferenceToggle = React.forwardRef<HTMLButtonElement, VideoconferenceToggleProps>(
  function VideoconferenceToggle({ videoEnabled, onToggle, label = 'Videoconference', testId = 'videoconference-toggle', className }, ref) {
    const [isSaving, setIsSaving] = React.useState(false)

    const handleChange = async (next: boolean) => {
      if (isSaving) return
      setIsSaving(true)
      try {
        await onToggle(next)
      } finally {
        setIsSaving(false)
      }
    }

    return (
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={testId} className="text-sm font-semibold text-primary">
          {label}
        </label>
        <Switch
          ref={ref}
          id={testId}
          data-testid={testId}
          checked={videoEnabled}
          disabled={isSaving}
          onCheckedChange={handleChange}
          className={className}
        />
      </div>
    )
  }
)
